/**
 * @file: useSystemModuleCards.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  InspectedItem,
  SiteInspectionCard,
} from '@euler/app/tv/components/inspection/types';
import { customIssueToReportItem } from '@euler/app/tv/functions/customIssueToReportItem';
import { getSiteInspectionDefectiveLevelPatched } from '@euler/app/tv/functions/getSiteInspectionDefectiveLevelPatched';
import { inspectedItemToReportItem } from '@euler/app/tv/functions/inspectedItemToReportItem';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import { InventoryQuery } from '@euler/functions/useInventory';
import { DefectiveLevel } from '@euler/model';
import {
  CustomIssue,
  SiteInspection,
  TaskDetail,
} from '@euler/model/task-detail';
import { comparerDesc } from '@euler/utils';
import { useMemo } from 'react';

export type SiteInspectionCardFilter =
  | {
      type: 'system-module';
      currentSystemModId: number;
    }
  | {
      type: 'defective-level';
      defectiveLevel: DefectiveLevel;
    }
  | {
      type: 'maintenance-advice';
      maintenanceAdvice: string;
    };

export const useSiteInspectionCards = (
  detail: TaskDetail,
  inventoryQuery: InventoryQuery,
  filter: SiteInspectionCardFilter,
) => {
  return useMemo<SiteInspectionCard[]>(() => {
    let inspectedSites: SiteInspection[] = [];
    let customIssues: CustomIssue[] = [];

    if (filter.type === 'system-module') {
      inspectedSites = detail.inspection.sites.filter(x => {
        const site = inventoryQuery.getSiteById(x.siteId);
        const modId = site?.primarySysModId ?? 0;
        return filter.currentSystemModId === modId;
      });
      customIssues =
        filter.currentSystemModId === 0
          ? detail.inspection.customIssues ?? []
          : [];
    } else if (filter.type === 'defective-level') {
      inspectedSites = detail.inspection.sites.filter(
        x =>
          getSiteInspectionDefectiveLevelPatched(x) === filter.defectiveLevel,
      );
      customIssues =
        detail.inspection.customIssues?.filter(
          x =>
            getDefectiveLevel(x.abnormalLevel, x.severityLevel) ===
            filter.defectiveLevel,
        ) ?? [];
    } else if (filter.type === 'maintenance-advice') {
      inspectedSites = detail.inspection.sites.filter(
        t =>
          t.items.some(x => x.maintenanceAdvice === filter.maintenanceAdvice) ||
          t.customIssues.some(
            x => x.maintenanceAdvice === filter.maintenanceAdvice,
          ),
      );
      customIssues =
        detail.inspection.customIssues?.filter(
          x => x.maintenanceAdvice === filter.maintenanceAdvice,
        ) ?? [];
    }

    const siteCards = inspectedSites
      .sort((x, y) =>
        comparerDesc(
          getSiteInspectionDefectiveLevelPatched(x),
          getSiteInspectionDefectiveLevelPatched(y),
        ),
      )
      .map<SiteInspectionCard>(inspectedSite => {
        const site = inventoryQuery.getSiteById(inspectedSite.siteId);
        return {
          type: 'inspected-site',
          key: `inspection-site-${inspectedSite.id}`,
          name: inspectedSite.name,
          inspectedSite,
          site,
          defectiveLevel: getSiteInspectionDefectiveLevelPatched(inspectedSite),
          items: [
            ...inspectedSite.items.map<InspectedItem>(inspectedItem => ({
              ...inspectedItemToReportItem({
                site,
                inspectedSite,
                inspectedItem,
              }),
              key: `item-${inspectedItem.id}`,
            })),
            ...inspectedSite.customIssues.map<InspectedItem>(customIssue => ({
              ...customIssueToReportItem({ customIssue }),
              key: `custom-issue-${customIssue.id}`,
            })),
          ].sort((x, y) => comparerDesc(x.severityLevel, y.severityLevel)),
        };
      });
    const customIssueCards = customIssues.map<SiteInspectionCard>(
      customIssue => ({
        type: 'custom-issue',
        key: `custom-issue-${customIssue.id}`,
        name: customIssue.siteName,
        customIssue,
        defectiveLevel: getDefectiveLevel(
          customIssue.abnormalLevel,
          customIssue.severityLevel,
        ),
        items: [
          {
            ...customIssueToReportItem({ customIssue }),
            key: `custom-issue-${customIssue.id}`,
          },
        ],
      }),
    );
    return [...siteCards, ...customIssueCards].sort((x, y) =>
      comparerDesc(x.defectiveLevel, y.defectiveLevel),
    );
  }, [
    filter,
    detail.inspection.sites,
    detail.inspection.customIssues,
    inventoryQuery,
  ]);
};
